import React from "react";
import classNames from "classnames";
import AsyncSelect from "react-select/async";
import Button from "../../buttons";
import FormGroup from "../FormGroup.js";
import FormLabel from "../FormLabel.js";
import FormInputGroup from "../FormInputGroup.js";
import { MultiValue, SingleValue } from "react-select";
import { ClientProjectSelectOption } from "./ClientProjectSelect.js";
import { DefReactSelectOption } from "../../../types/DefReactSelectOption.js";
import { PagedResults } from "../../../types/PagedResults.js";
import { MasterAccountProjectType } from "../../../types/MasterAccountProjectType.js";
import { openCreateProjectModal } from "../../modals/client-project-create";
import { useLazyFetchMasterAccountProjectsListQuery } from "../../../services/clientprojects";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

export type ClientProjectSearchSelectProps = {
    allowCreate?: boolean,
    adviserId: string,
    className?: any,
    divisionId: string,
    error?: string | undefined,
    isClearable?: boolean,
    isDisabled?: boolean,
    isMulti?: boolean,
    label?: string,
    masterAccountId: string,
    onChange: (newValue: MultiValue<DefReactSelectOption<MasterAccountProjectType>> | SingleValue<DefReactSelectOption<MasterAccountProjectType>>) => void,
    userId: string,
    value: string | string[] | null
};

export const ClientProjectSearchSelect = React.forwardRef<any, ClientProjectSearchSelectProps>(({ 
    allowCreate = true,
    adviserId,
    className,
    isClearable = true,
    isDisabled = false,
    isMulti = false,
    label = "Project",
    masterAccountId,
    divisionId,
    onChange = () => {},
    userId,
    value
} : Readonly<ClientProjectSearchSelectProps>, ref) => {
    const componentId = React.useId();
    const [additionalProjects, setAdditionalProjects] = React.useState<MasterAccountProjectType[]>([]);
    const [cacheKey, setCacheKey] = React.useState<number>(new Date().valueOf());
    const [load, { data }] = useLazyFetchMasterAccountProjectsListQuery();

    // called to determine what the default value supplied to the select would be
    const buildDefaultValue = () : DefReactSelectOption<MasterAccountProjectType> | DefReactSelectOption<MasterAccountProjectType>[] | null => {
        if (!value || !data) {
            return null;
        }

        const selectedValue = value;
        const { results } = data as PagedResults<DefReactSelectOption<MasterAccountProjectType>>;
        const resultSets : DefReactSelectOption<MasterAccountProjectType>[] = [
            ...additionalProjects.map((el) => ({ label: el.description, value: el.projectId, data: el, isDisabled: false } as DefReactSelectOption<MasterAccountProjectType>)), 
            ...results
        ];

        if (resultSets.length === 0) {
            return null;
        }

        if (isMulti === true) {
            const selectedValues : string[] = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
            return resultSets.filter(({ value }) => selectedValues.includes(value));
        }

        const foundResult = resultSets.find(({ value }) => value === selectedValue) ?? null;
        return foundResult;
    };

    // called by the async react select component to get the list of projects tied to a master account
    const loadOptions = (input: string, callback: (options: DefReactSelectOption<MasterAccountProjectType>[]) => void) => {
        const newCacheKey = new Date().valueOf();
        load({ cacheKey: newCacheKey, masterAccountId, searchText: input, page: 1, limit: 50 })
            .then(
                ({ data }: { data: PagedResults<DefReactSelectOption<MasterAccountProjectType>> }) => {
                    if (data && data.results.length > 0) {
                        callback(data.results);
                    }
                    else {
                        callback([]);
                    }
                }
            )
            .finally(() => {
                setCacheKey(() => newCacheKey);
            });
    };

    const onCreateProjectClicked = () => {
        if (!allowCreate) {
            return;
        }
        openCreateProjectModal({ 
            adviserId: adviserId,
            divisionId,
            masterAccountId,
            userId: userId
        }).then(
            (r) => {
                if (r.status >= 300 || !r.project) {
                    return;
                }
                
                setAdditionalProjects(prev => [...prev, r.project!]);
                onChange({ 
                    label: r.project!.description,
                    value: r.project!.projectId,
                    isDisabled: false,
                    isFixed: false,
                    data: r.project! 
                } as SingleValue<DefReactSelectOption<MasterAccountProjectType>>);
            }
        )
    };

    const displayValue = React.useMemo(() => buildDefaultValue(), [ data, isMulti, value ]);
    return (
        <FormGroup>
            <FormLabel htmlFor={componentId}>{label}</FormLabel>
            <FormInputGroup>
                <AsyncSelect 
                    id={componentId}
                    className={classNames('flex-fill', className)}
                    ref={ref}
                    cacheOptions={true}
                    defaultOptions={true}
                    loadOptions={loadOptions}
                    isClearable={isClearable}
                    isDisabled={isDisabled}
                    isMulti={isMulti}
                    value={displayValue}
                    onChange={(e) => onChange(e)}
                    components={{
                        Option: ClientProjectSelectOption
                    }}
                />
                {allowCreate && (
                    <Button variant="success" type="button" className="text-nowrap" disabled={isDisabled} onClick={onCreateProjectClicked}>
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        New Project
                    </Button>
                )}
            </FormInputGroup>
        </FormGroup>
    );
});